import * as React from 'react';
import { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { TAuthState } from '../types/Auth';
import { useSelector } from 'react-redux';
import { IAppState } from '../store/Store';
import logo_da from '../assets/images/da_iphone.png';
import logo_en from '../assets/images/en_iphone.png';
import logo_da_26 from '../assets/images/da_iphone26_2.png';
import logo_en_26 from '../assets/images/en_iphone26_2.png';
import logo_example from '../assets/images/iphone-desktop.png';
import { isAndroid, isIOS, isMobile, getUA } from 'react-device-detect';
import Button from '@mui/material/Button';
import { deferredPrompt, clearDeferredPrompt} from '../hooks/usePWAHandler';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

const useStyles = makeStyles((theme: Theme) => ({
  popover: {
    position: 'fixed',
    left: '50%',
    bottom: '15px',
    transform: 'translateX(-50%)',
    width: '90%',
    border: '1px solid #ddd',
  },
  content: {
    paddingBottom: '0 !important',
  },
  cardArea: {
    '&:hover': {
      opacity: '1',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  button: {
    border: '0',
    borderColor: 'transparent !important',
  },
  dialogContainer: {
    alignItems: 'flex-end',
  },
  dialogPaper: {
    margin: 0,
    width: '90%',
    marginBottom: '15px',
  },
}));

const AddToHomeScreen: React.FC = (): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const [show, setShow] = useState(isMobile && (isIOS || isAndroid));
  const authState: TAuthState = useSelector<IAppState, TAuthState>(
    (state) => state.authState
  );
  const getTrueIOSVersion = (): number => {
    const match = getUA.match(/Version\/([\d.]+)/);
    return match ? parseFloat(match[1]) : 0;
  };
  const trueVersion = getTrueIOSVersion();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isMobile && isAndroid) {
      setShow(!!deferredPrompt);
    }
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      clearDeferredPrompt();
      setShow(false);
    }
  };

  const handlePopupClose = () => {
    setShow(false);
  };

  return (
    <>
      {show && (
        <Dialog
          open={show}
          onClose={() => handlePopupClose()}
          classes={{
            container: classes.dialogContainer,
            paper: classes.dialogPaper,
          }}
        >
          <DialogTitle>
            {t('add-to-home-screen.title')}
            <IconButton
              aria-label='close'
              className={classes.closeButton}
              onClick={() => handlePopupClose()}
              size="large">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className={classes.content}>
            {isAndroid && (
              <>
                <p>
                  {t('add-to-home-screen.content.android', {
                    company: authState.data?.solution.company,
                    interpolation: {
                      escapeValue: false,
                    },
                  })}
                </p>
                <img src={logo_example} alt='' />
                {deferredPrompt && (
                  <Button
                    variant="contained"
                    color="secondary"
                    style={{ marginTop: "15px", marginBottom: "15px" }}
                    onClick={handleInstallClick}
                    fullWidth
                  >
                    {t('add-to-home-screen.content.android.install-button')}
                  </Button>
                )}
              </>
            )}
            {isIOS && (
              <>
                <p>
                  {t('add-to-home-screen.content.ios', {
                    company: authState.data?.solution.company,
                    interpolation: {
                      escapeValue: false,
                    },
                  })}
                </p>
                <img src={logo_example} alt='' />
                {i18n.language === 'en' ? (
                  <img src={trueVersion >= 26.2 ? logo_en_26 : logo_en} alt='' />
                ) : (
                  <img src={trueVersion >= 26.2 ? logo_da_26 : logo_da} alt='' />
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AddToHomeScreen;
